import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  getCustomerTypes,
  getAllCustomers,
  getSalesExecutives,
} from '../services/customer_service'
import {
  deleteOrder,
  getPublishedOrders,
  getTotalRevenue,
} from '../services/order_service'
import { useAuth } from '../context/AuthContext'
import './OrderPublished.css'

const emptyFilters = {
  fromDate: '',
  toDate: '',
  fromAmount: '',
  toAmount: '',
  status: '',
  salesExecutive: '',
  customerType: '',
  customer: '',
}

const getValue = (record, keys, fallback = '-') =>
  keys
    .map((key) => record?.[key])
    .find((value) => value !== undefined && value !== null && value !== '') ??
  fallback
const getOptionValue = (option) =>
  typeof option === 'object'
    ? getValue(option, ['id', 'value', 'customerid', 'customer_id', 'userid'])
    : option
const getOptionLabel = (option) =>
  typeof option === 'object'
    ? getValue(option, [
        'name',
        'label',
        'cust_name',
        'customer_name',
        'sales_exec_name',
        'sales_executive',
        'customertype',
        'type',
      ])
    : option
const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
const formatDate = (value) => {
  if (!value || value === '-') return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
}
const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: 'GHS',
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)
const getOrderId = (order) =>
  getValue(order, ['orderid', 'orderId', 'order_id', 'id'])
const getOrderDate = (order) =>
  getValue(order, ['request_date', 'order_date', 'orderDate', 'created_at'])
const getOrderAmount = (order) =>
  getValue(order, ['nettotal', 'netAmount', 'total', 'amount', 'net'], 0)

function OrderPublished() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canCreateOrder = hasPermission('addOrder')
  const canEditOrder = hasPermission('editOrder')
  const [searchParams] = useSearchParams()
  const [linkedFilters] = useState(() => ({
    ...emptyFilters,
    salesExecutive: searchParams.get('salesExecutive') || '',
    fromDate: searchParams.get('fromDate') || '',
    toDate: searchParams.get('toDate') || '',
  }))
  const [filters, setFilters] = useState(linkedFilters)
  const [appliedFilters, setAppliedFilters] = useState(linkedFilters)
  const [isQueryOpen, setIsQueryOpen] = useState(false)
  const [orders, setOrders] = useState([])
  const [salesExecutives, setSalesExecutives] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [customers, setCustomers] = useState([])
  const [totalRevenue, setTotalRevenue] = useState(0)
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [cursorHistory, setCursorHistory] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingOrderId, setDeletingOrderId] = useState(null)
  const [pendingDeleteOrder, setPendingDeleteOrder] = useState(null)

  const loadOrders = async (
    cursor = null,
    page = 1,
    query = appliedFilters,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getPublishedOrders({ cursor, page, ...query })
      setOrders(result.orders)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    Promise.all([
      getSalesExecutives(),
      getCustomerTypes(),
      getAllCustomers(''),
      getTotalRevenue(),
    ])
      .then(
        ([
          loadedSalesExecutives,
          loadedCustomerTypes,
          loadedCustomers,
          loadedTotalRevenue,
        ]) => {
          setSalesExecutives(loadedSalesExecutives)
          setCustomerTypes(loadedCustomerTypes)
          setCustomers(loadedCustomers)
          setTotalRevenue(loadedTotalRevenue)
        },
      )
      .catch((requestError) => setError(requestError.message))
    loadOrders(null, 1, linkedFilters)
  }, [])

  const filteredCustomers = customers

  const updateFilter = (event) => {
    const { name, value } = event.target
    setFilters((current) => ({
      ...current,
      [name]: value,
      ...(name === 'customerType' ? { customer: '' } : {}),
    }))
  }
  const handleCustomerTypeChange = async (event) => {
    const customerType = event.target.value
    setFilters((current) => ({
      ...current,
      customerType,
      customer: '',
    }))
    setError('')

    try {
      setCustomers(await getAllCustomers(customerType))
    } catch (requestError) {
      setCustomers([])
      setError(requestError.message)
    }
  }
  const searchOrders = (event) => {
    event.preventDefault()
    setAppliedFilters(filters)
    setCursorHistory([])
    loadOrders(null, 1, filters)
  }
  const clearFilters = () => {
    setFilters(emptyFilters)
    setAppliedFilters(emptyFilters)
    setCursorHistory([])
    loadOrders(null, 1, emptyFilters)
  }
  const goToNextPage = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadOrders(nextCursor, nextPage || currentPage, filters)
  }
  const goToPreviousPage = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadOrders(previous.cursor, previous.page, filters)
  }
  const removeOrder = async (order) => {
    const status = normalize(getValue(order, ['status', 'order_status']))
    if (status !== 'requested') return

    setPendingDeleteOrder(order)
  }

  const confirmDeleteOrder = async () => {
    if (!pendingDeleteOrder) return

    const orderId = getOrderId(pendingDeleteOrder)

    setDeletingOrderId(orderId)
    setError('')
    try {
      await deleteOrder(orderId)
      setPendingDeleteOrder(null)
      await loadOrders(currentCursor, currentPage, appliedFilters)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setDeletingOrderId(null)
    }
  }

  return (
    <div className='orders-page'>
      <nav className='orders-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Published orders</strong>
      </nav>
      <header className='orders-page__header'>
        <div>
          <span className='app-content__eyebrow'>Sales</span>
          <h1>Published orders</h1>
          <p>
            Review published orders and narrow the list by date, value, and
            account.
          </p>
        </div>
      </header>
      {error && (
        <div className='orders-page__error' role='alert'>
          {error}
        </div>
      )}

      <section
        className='orders-query-card'
        aria-labelledby='orders-query-title'
      >
        <button
          className='orders-query-card__toggle'
          type='button'
          onClick={() => setIsQueryOpen((open) => !open)}
          aria-expanded={isQueryOpen}
        >
          <span>
            <span className='app-content__eyebrow'>Refine results</span>
            <strong id='orders-query-title'>Order query</strong>
          </span>
          <span aria-hidden='true'>{isQueryOpen ? '−' : '+'}</span>
        </button>
        {isQueryOpen && (
          <form className='orders-query-form' onSubmit={searchOrders}>
            <fieldset className='orders-query-form__range'>
              <legend>Date range</legend>
              <label>
                From date
                <input
                  name='fromDate'
                  type='date'
                  value={filters.fromDate}
                  onChange={updateFilter}
                />
              </label>
              <label>
                To date
                <input
                  name='toDate'
                  type='date'
                  value={filters.toDate}
                  onChange={updateFilter}
                />
              </label>
            </fieldset>
            <fieldset className='orders-query-form__range'>
              <legend>Amount range</legend>
              <label>
                From amount
                <input
                  name='fromAmount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={filters.fromAmount}
                  onChange={updateFilter}
                />
              </label>
              <label>
                To amount
                <input
                  name='toAmount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={filters.toAmount}
                  onChange={updateFilter}
                />
              </label>
            </fieldset>
            <label>
              Order status
              <select
                name='status'
                value={filters.status}
                onChange={updateFilter}
              >
                <option value=''>All statuses</option>
                <option value='requested'>Requested</option>
                <option value='approved'>Approved</option>
                <option value='delivered'>Delivered</option>
                <option value='paid'>Paid</option>
              </select>
            </label>
            <label>
              Sales executive
              <select
                name='salesExecutive'
                value={filters.salesExecutive}
                onChange={updateFilter}
              >
                <option value=''>All sales executives</option>
                {salesExecutives.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Customer type
              <select
                name='customerType'
                value={filters.customerType}
                onChange={handleCustomerTypeChange}
              >
                <option value=''>All customer types</option>
                {customerTypes.map((option) => (
                  <option
                    key={getOptionValue(option)}
                    value={getOptionValue(option)}
                  >
                    {getOptionLabel(option)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Customer
              <select
                name='customer'
                value={filters.customer}
                onChange={updateFilter}
              >
                <option value=''>All customers</option>
                {filteredCustomers.map((customer) => (
                  <option
                    key={getValue(customer, ['customerid', 'id'])}
                    value={getValue(customer, ['customerid', 'id'])}
                  >
                    {getValue(customer, ['cust_name', 'name', 'company'])}
                  </option>
                ))}
              </select>
            </label>
            <div className='orders-query-form__actions'>
              <button
                className='orders-button orders-button--primary'
                type='submit'
              >
                Search
              </button>
              <button
                className='orders-button'
                type='button'
                onClick={clearFilters}
              >
                Clear
              </button>
            </div>
          </form>
        )}
      </section>

      <section className='orders-revenue-card' aria-label='Yearly revenue'>
        <span className='app-content__eyebrow'>Annual performance</span>
        <span className='orders-revenue-card__label'>
          Total revenue for the year
        </span>
        <strong>{formatCurrency(totalRevenue)}</strong>
      </section>

      <section
        className='orders-table-card'
        aria-labelledby='published-orders-title'
      >
        <div className='orders-table-card__header'>
          <div>
            <span className='app-content__eyebrow'>Order register</span>
            <h2 id='published-orders-title'>Published order list</h2>
            <p>{orders.length} orders on this page</p>
          </div>
          {canCreateOrder && (
            <button
              className='orders-button orders-button--primary'
              type='button'
              onClick={() => navigate('/sales/orders/new')}
            >
              + Create new order
            </button>
          )}
        </div>
        <div className='orders-table-wrap'>
          <table className='orders-table'>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Sales executive</th>
                <th>Status</th>
                <th>Amount</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='orders-table__empty' colSpan='7'>
                    Loading orders...
                  </td>
                </tr>
              ) : orders.length ? (
                orders.map((order, index) => (
                  <tr key={`${getOrderId(order)}-${index}`}>
                    <td>
                      <strong>{getOrderId(order)}</strong>
                    </td>
                    <td>{formatDate(getOrderDate(order))}</td>
                    <td>
                      {getValue(order, [
                        'customer_name',
                        'customerName',
                        'cust_name',
                        'customer',
                      ])}
                    </td>
                    <td>
                      {getValue(order, [
                        'sales_exec_name',
                        'salesExecutive',
                        'sales_executive',
                        'sales_exec',
                      ])}
                    </td>
                    <td>
                      <span className='orders-status'>
                        {getValue(
                          order,
                          ['status', 'order_status'],
                          'Published',
                        )}
                      </span>
                    </td>
                    <td className='orders-table__amount'>
                      {formatCurrency(getOrderAmount(order))}
                    </td>
                    <td>
                      <div className='orders-actions'>
                        {canEditOrder &&
                          normalize(
                            getValue(order, ['status', 'order_status']),
                          ) === 'requested' && (
                            <button
                              type='button'
                              onClick={() =>
                                navigate(
                                  `/sales/orders/${getOrderId(order)}/edit`,
                                )
                              }
                            >
                              Edit
                            </button>
                          )}
                        <button
                          type='button'
                          onClick={() =>
                            navigate(`/sales/orders/${getOrderId(order)}`, {
                              state: { order },
                            })
                          }
                        >
                          View
                        </button>
                        {canEditOrder &&
                          normalize(
                            getValue(order, ['status', 'order_status']),
                          ) === 'requested' && (
                            <button
                              className='orders-actions__delete'
                              type='button'
                              onClick={() => removeOrder(order)}
                              disabled={deletingOrderId === getOrderId(order)}
                            >
                              {deletingOrderId === getOrderId(order)
                                ? 'Deleting...'
                                : 'Delete'}
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='orders-table__empty' colSpan='7'>
                    No published orders match the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='orders-pagination'>
          <span>Showing up to 20 orders</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousPage}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextPage}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>
      {pendingDeleteOrder && (
        <div
          className='orders-modal__backdrop'
          role='presentation'
          onMouseDown={(event) =>
            event.target === event.currentTarget && setPendingDeleteOrder(null)
          }
        >
          <section
            className='orders-modal'
            role='alertdialog'
            aria-modal='true'
            aria-labelledby='delete-order-title'
            aria-describedby='delete-order-description'
          >
            <div className='orders-modal__header'>
              <div>
                <span className='app-content__eyebrow'>Delete order</span>
                <h2 id='delete-order-title'>Delete published order?</h2>
              </div>
              <button
                type='button'
                onClick={() => setPendingDeleteOrder(null)}
                aria-label='Close delete confirmation'
                disabled={deletingOrderId === getOrderId(pendingDeleteOrder)}
              >
                x
              </button>
            </div>
            <p
              className='orders-modal__description'
              id='delete-order-description'
            >
              Order {getOrderId(pendingDeleteOrder)} will be permanently
              deleted. This action cannot be undone.
            </p>
            <div className='orders-modal__actions'>
              <button
                type='button'
                onClick={() => setPendingDeleteOrder(null)}
                disabled={deletingOrderId === getOrderId(pendingDeleteOrder)}
              >
                Cancel
              </button>
              <button
                className='orders-modal__delete-button'
                type='button'
                onClick={confirmDeleteOrder}
                disabled={deletingOrderId === getOrderId(pendingDeleteOrder)}
              >
                {deletingOrderId === getOrderId(pendingDeleteOrder)
                  ? 'Deleting...'
                  : 'Delete order'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default OrderPublished
